import {Button} from '@hm/fundamental'
import {useTranslation} from 'react-i18next'
import * as XLSX from 'xlsx'

import {DrillingPosition, ShutdownDrillingDTO} from '@refra/modules/refra-planning/types'

interface DownloadDrillingResultsExcelTemplateProps {
  shutdownId: number
  shutdownName: string
  drillingPositions: DrillingPosition[]
  kilnLength: number
  drillings: ShutdownDrillingDTO[]
}

export const DownloadDrillingResultsExcelTemplate: React.FC<
  DownloadDrillingResultsExcelTemplateProps
> = ({shutdownId, shutdownName, drillingPositions, kilnLength, drillings}) => {
  const {t} = useTranslation()

  const handleDownloadTemplate = () => {
    // Generate Excel template data based on drilling positions
    const enabledPositions = drillingPositions.filter((p) => p.isEnabled)

    // Create a lookup map for existing drilling values: Map<meter, Map<positionId, depth>>
    const drillingLookup = new Map<number, Map<number, number>>()
    for (const drilling of drillings) {
      if (!drillingLookup.has(drilling.meter)) {
        drillingLookup.set(drilling.meter, new Map())
      }
      drillingLookup.get(drilling.meter)?.set(drilling.drillingPosition.id, drilling.depth)
    }

    // Create legend rows
    const legendRows = [
      [t('refraPlanning.drilling.legend.title')],
      [t('refraPlanning.drilling.legend.meterReadOnly')],
      [t('refraPlanning.drilling.legend.namesNotChanged')],
      [t('refraPlanning.drilling.legend.rowRemoved')],
      [t('refraPlanning.drilling.legend.emptyCell')],
      [''] // Empty row before header
    ]

    // Create header row: Meter + Drilling Position + angle names
    const headers = [
      t('common.meter'),
      t('common.drillingPosition'),
      ...enabledPositions.map((p) => p.name)
    ]

    // Create data rows: one row per meter (1 to kilnLength) with existing values
    // Meter values range from 1 to kilnLength (not 0), matching the drilling data structure
    const dataRows: (string | number)[][] = []
    for (let meter = 1; meter <= kilnLength; meter++) {
      const meterDrillings = drillingLookup.get(meter)
      const row: (string | number)[] = [
        meter,
        meter, // Drilling Position matches the meter value
        ...enabledPositions.map((position) => {
          const depth = meterDrillings?.get(position.id)
          return depth !== undefined ? depth : ''
        })
      ]
      dataRows.push(row)
    }

    // Combine legend, headers and data
    const allRows = [...legendRows, headers, ...dataRows]

    // Create workbook and worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(allRows)

    // Set column widths
    worksheet['!cols'] = [
      {wch: 10}, // Meter
      {wch: 18}, // Drilling Position
      ...enabledPositions.map(() => ({wch: 12})) // Angle columns
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Drilling Results')

    // Sanitize shutdown name for filename (remove special characters)
    const safeShutdownName = shutdownName.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 50)

    // Generate Excel file and download
    XLSX.writeFile(workbook, `drilling-results-${safeShutdownName}-${shutdownId}.xlsx`)
  }

  return (
    <Button
      variant="tertiary"
      onClick={handleDownloadTemplate}
      data-cy="download_drilling_results_template"
    >
      {t('refraPlanning.drilling.downloadExcelTemplate')}
    </Button>
  )
}
